import { supabase } from './supabaseClient';

/**
 * Authentication service for handling user login, registration, and session management
 */

/**
 * Sign up a new user with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @param {string} userType - User type ('artist' or 'venue')
 * @param {Object} profileData - Optional profile data to create during registration
 * @param {Object} userMetadata - Optional user metadata to store with the user
 * @returns {Promise} - Supabase auth response
 */
export const signUp = async (email, password, userType = 'artist', profileData = null, userMetadata = null) => {
  try {
    // Prepare user metadata
    const metadata = {
      user_type: userType,
      // Set the profile_setup_required flag to true by default
      profile_setup_required: true,
      ...(userMetadata || {})
    };
    
    console.log('Signing up user with metadata:', JSON.stringify(metadata, null, 2));
    console.log('Profile data:', JSON.stringify(profileData, null, 2));
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    
    if (error) {
      console.error('Error during signup:', error.message);
      throw error;
    }
    
    // If signup was successful, create a profile for the user
    if (data.user) {
      console.log('User created successfully, creating profile for user:', data.user.id);
      
      // Ensure profileData has the uuid field
      if (profileData) {
        profileData.uuid = data.user.id;
        
        // Add timestamps if not present
        if (!profileData.created_at) profileData.created_at = new Date().toISOString();
        if (!profileData.updated_at) profileData.updated_at = new Date().toISOString();
        
        // Directly insert into profiles table
        console.log('Inserting profile with data:', JSON.stringify(profileData, null, 2));
        
        const { data: profileData2, error: profileError } = await supabase
          .from('profiles')
          .insert([profileData])
          .select();
        
        if (profileError) {
          console.error('Error creating profile:', profileError.message);
          console.error('Supabase error details:', JSON.stringify(profileError, null, 2));
        } else {
          console.log('Profile created successfully:', profileData2);
        }
      } else {
        // Create a basic profile with just the user type
        console.log('Creating basic profile with user type:', userType);
        const profileResult = await createUserProfile(data.user.id, userType);
        console.log('Basic profile creation result:', profileResult);
      }
    } else {
      console.warn('User object not found in signup response, profile not created');
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error signing up:', error.message);
    return { data: null, error };
  }
};

/**
 * Sign in an existing user with email and password
 * @param {string} email - User's email address
 * @param {string} password - User's password
 * @returns {Promise} - Supabase auth response
 */
export const signIn = async (email, password) => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error signing in:', error.message);
    return { data: null, error };
  }
};

/**
 * Sign out the current user
 * @returns {Promise} - Supabase auth response
 */
export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error signing out:', error.message);
    return { error };
  }
};

/**
 * Get the current user session
 * @returns {Promise} - Current session data
 */
export const getCurrentSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return { session: data.session, error: null };
  } catch (error) {
    console.error('Error getting session:', error.message);
    return { session: null, error };
  }
};

/**
 * Get the current user
 * @returns {Promise} - Current user data
 */
export const getCurrentUser = async () => {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) {
      console.error('Error getting user:', error.message);
      throw error;
    }
    if (!user) {
      console.error('Error getting user: Auth session missing!');
      return { user: null, error: new Error('Auth session missing!') };
    }
    return { user, error: null };
  } catch (error) {
    console.error('Error getting user:', error.message);
    return { user: null, error };
  }
};

/**
 * Reset password for a user
 * @param {string} email - User's email address
 * @returns {Promise} - Supabase auth response
 */
export const resetPassword = async (email) => {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error resetting password:', error.message);
    return { data: null, error };
  }
};

/**
 * Update user password
 * @param {string} newPassword - New password
 * @returns {Promise} - Supabase auth response
 */
export const updatePassword = async (newPassword) => {
  try {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error updating password:', error.message);
    return { data: null, error };
  }
};

/**
 * Create a user profile after registration
 * @param {string} userId - User's UUID
 * @param {string} userType - User type ('artist' or 'venue')
 * @returns {Promise} - Created profile data
 */
export const createUserProfile = async (userId, userType = 'artist') => {
  try {
    if (!userId) {
      console.error('Error creating profile: Missing user ID');
      return { data: null, error: new Error('Missing user ID') };
    }
    
    console.log('Creating profile for user ID:', userId, 'with type:', userType);
    
    // First check if profile already exists
    const { data: existingProfiles, error: checkError } = await supabase
      .from('profiles')
      .select('id, uuid')
      .eq('uuid', userId);
    
    if (checkError) {
      console.error('Error checking if profile exists:', checkError.message);
      throw checkError;
    }
    
    // If profile already exists, don't create a new one
    if (existingProfiles && existingProfiles.length > 0) {
      console.log('Profile already exists for user:', userId);
      return { data: existingProfiles[0], error: null };
    }
    
    // Create new profile
    const { error } = await supabase
      .from('profiles')
      .insert([
        {
          uuid: userId,
          user_type: userType || 'artist',  // Default to artist if not specified
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ]);
    
    if (error) {
      console.error('Error creating user profile:', error.message);
      console.error('Supabase error details:', JSON.stringify(error));
      throw error;
    }
    
    // Get the newly created profile
    const { data: newProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('uuid', userId);
    
    if (fetchError) {
      console.error('Error fetching new profile:', fetchError.message);
      throw fetchError;
    }
    
    console.log('Profile created successfully:', newProfile);
    return { data: newProfile && newProfile.length > 0 ? newProfile[0] : null, error: null };
  } catch (error) {
    console.error('Error creating user profile:', error.message);
    return { data: null, error };
  }
};

/**
 * Create a user profile with complete profile data after registration
 * @param {string} userId - User's UUID
 * @param {Object} profileData - Complete profile data
 * @returns {Promise} - Created profile data
 */
export const createUserProfileWithData = async (userId, profileData) => {
  try {
    if (!userId) {
      console.error('Error creating profile: Missing user ID');
      return { data: null, error: new Error('Missing user ID') };
    }
    
    if (!profileData) {
      console.error('Error creating profile: Missing profile data');
      return { data: null, error: new Error('Missing profile data') };
    }
    
    console.log('Creating complete profile for user ID:', userId);
    
    // First check if profile already exists
    const { data: existingProfiles, error: checkError } = await supabase
      .from('profiles')
      .select('id, uuid')
      .eq('uuid', userId);
    
    if (checkError) {
      console.error('Error checking if profile exists:', checkError.message);
      throw checkError;
    }
    
    // If profile already exists, don't create a new one
    if (existingProfiles && existingProfiles.length > 0) {
      console.log('Profile already exists for user:', userId);
      
      // Update the existing profile with the new data
      const { data: updatedProfile, error: updateError } = await supabase
        .from('profiles')
        .update({
          ...profileData,
          updated_at: new Date().toISOString()
        })
        .eq('uuid', userId)
        .select(); // Add select() to return the updated data
      
      if (updateError) {
        console.error('Error updating existing profile:', updateError.message);
        throw updateError;
      }
      
      // Return the updated profile data, not the old data
      return { data: updatedProfile && updatedProfile.length > 0 ? updatedProfile[0] : null, error: null };
    }
    
    // Create new profile with complete data
    const profileToInsert = {
      uuid: userId,
      ...profileData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    console.log('Inserting profile with data:', profileToInsert);
    
    const { error } = await supabase
      .from('profiles')
      .insert([profileToInsert])
      .select();
    
    if (error) {
      console.error('Error creating user profile with data:', error.message);
      console.error('Supabase error details:', JSON.stringify(error));
      throw error;
    }
    
    // Get the newly created profile
    const { data: newProfile, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('uuid', userId);
    
    if (fetchError) {
      console.error('Error fetching new profile:', fetchError.message);
      throw fetchError;
    }
    
    console.log('Complete profile created successfully:', newProfile);
    return { data: newProfile && newProfile.length > 0 ? newProfile[0] : null, error: null };
  } catch (error) {
    console.error('Error creating user profile with data:', error.message);
    return { data: null, error };
  }
};

/**
 * Get user profile by user ID
 * @param {string} userId - User's UUID
 * @returns {Promise} - User profile data
 */
export const getUserProfile = async (userId) => {
  try {
    if (!userId) {
      console.error('Error getting user profile: Missing user ID');
      return { profile: null, error: new Error('Missing user ID') };
    }
    
    console.log('Getting profile for user ID:', userId);
    
    // First try to get by uuid
    const { data: profileByUuid, error: uuidError } = await supabase
      .from('profiles')
      .select('*')
      .eq('uuid', userId);
    
    if (uuidError) {
      console.error('Error getting profile by UUID:', uuidError.message);
      throw uuidError;
    }
    
    if (profileByUuid && profileByUuid.length > 0) {
      return { profile: profileByUuid[0], error: null };
    }
    
    console.log('No profile found with UUID, checking by id');
    
    // Fallback to id if uuid doesn't work
    const { data: profileById, error: idError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId);
    
    if (idError) {
      console.error('Error getting profile by ID:', idError.message);
      throw idError;
    }
    
    if (profileById && profileById.length > 0) {
      return { profile: profileById[0], error: null };
    }
    
    console.log('No profile found for user ID:', userId);
    return { profile: null, error: null };
  } catch (error) {
    console.error('Error getting user profile:', error.message);
    return { profile: null, error };
  }
};

/**
 * Update user profile
 * @param {string} userId - User's UUID
 * @param {object} profileData - Profile data to update
 * @returns {Promise} - Supabase response
 */
/**
 * Update user metadata
 * @param {string} userId - User's UUID
 * @param {object} metadata - Metadata to update
 * @returns {Promise} - Supabase response
 */
export const updateUserMetadata = async (userId, metadata) => {
  try {
    console.log('Updating user metadata for user:', userId);
    console.log('Metadata to update:', JSON.stringify(metadata, null, 2));
    
    // Use the client-side updateUser API to update metadata
    const { data, error } = await supabase.auth.updateUser({
      data: metadata
    });
    
    if (error) {
      console.error('Error updating user metadata:', error.message);
      throw error;
    }
    
    console.log('User metadata updated successfully:', data);
    return { data, error: null };
  } catch (error) {
    console.error('Error updating user metadata:', error.message);
    return { data: null, error };
  }
};

export const updateUserProfile = async (userId, profileData) => {
  try {
    if (!userId) {
      console.error('Error updating user profile: Missing user ID');
      return { data: null, error: new Error('Missing user ID') };
    }
    
    // Add updated_at timestamp
    profileData.updated_at = new Date().toISOString();
    
    console.log('Updating profile for user ID:', userId);
    
    // First check if profile exists
    const { data: existingProfiles, error: checkError } = await supabase
      .from('profiles')
      .select('id, uuid')
      .eq('uuid', userId);
    
    if (checkError) {
      console.error('Error checking if profile exists:', checkError.message);
      throw checkError;
    }
    
    if (!existingProfiles || existingProfiles.length === 0) {
      console.error('No profile found for user ID:', userId);
      return { data: null, error: new Error('Profile not found') };
    }
    
    // Update existing profile
    const existingProfile = existingProfiles[0];
    console.log('Updating existing profile with ID:', existingProfile.id);
    
    const { data, error } = await supabase
      .from('profiles')
      .update(profileData)
      .eq('uuid', userId)
      .select();
    
    if (error) {
      console.error('Error updating user profile:', error.message);
      console.error('Supabase error details:', JSON.stringify(error));
      throw error;
    }
    
    return { data, error: null };
  } catch (error) {
    console.error('Error updating user profile:', error.message);
    return { data: null, error };
  }
};
